import { type SessionSummaryQuery } from "@crowdcoursing/api/graphql";
import { Paper, Typography } from "@mui/material";
import { type DateTime } from "luxon";

import { type MeetingTimeData } from "../../organisms/AddEventTimesModal/AddEventTimesModal.types";
import { RSVPManagement } from "../../organisms/RSVPManagement/RSVPManagement";
import { SelectedDayCard } from "../SelectedDayCard/SelectedDayCard";

// User-facing strings
const STRINGS = {
  EVENT_TIME_DETAILS: "Event Time Details",
  SELECT_EVENT_TIME: "Select an event time from the calendar to view details and RSVP options",
} as const;

type EventFromQuery = NonNullable<SessionSummaryQuery["session"]>;

interface CalendarEventDetailsProps {
  selectedDate?: DateTime;
  meetingTimes: {
    id: string;
    beginMeetingTime: string;
    endMeetingTime: string;
    capacity?: number;
    isPendingApproval?: boolean;
    seriesId?: string;
    series?: {
      id: string;
      name: string;
      description: string;
    };
  }[];
  selectedMeetingTime?: {
    id: string;
    startTime: string;
    endTime: string;
  };
  onSelectMeetingTime: (meetingTime: { id: string; startTime: string; endTime: string } | undefined) => void;
  event: EventFromQuery;
  onCreateMeetingTimes: (meetingTimes: MeetingTimeData[], eventId: string) => void;
  isCreatingMeetingTimes?: boolean;
  onRSVP: (sessionId: string) => void;
  onCancelRSVP: (sessionId: string) => void;
  onApprovePending?: (meetingTimeId: string) => void | Promise<void>;
  isApprovingPending?: boolean;
  isUserAttending?: boolean;
  attendees?: {
    email: string | null;
    firstName: string | null;
    id: string;
    isVetted: boolean;
    lastName: string | null;
  }[];
  onDeleteMeetingTime?: (meetingTimeId: string) => void;
}

export const CalendarEventDetails = ({
  selectedDate,
  meetingTimes,
  selectedMeetingTime,
  onSelectMeetingTime,
  event,
  onCreateMeetingTimes,
  isCreatingMeetingTimes,
  onRSVP,
  onCancelRSVP,
  onApprovePending,
  isApprovingPending,
  isUserAttending,
  attendees = [],
  onDeleteMeetingTime,
}: CalendarEventDetailsProps) => {
  const selectedMeetingTimeDetails = selectedMeetingTime
    ? meetingTimes.find((mt) => mt.id === selectedMeetingTime.id)
    : undefined;

  return (
    <>
      {selectedDate && (
        <SelectedDayCard
          selectedDate={selectedDate}
          meetingTimes={meetingTimes}
          selectedMeetingTimeId={selectedMeetingTime?.id}
          onSelectMeetingTime={onSelectMeetingTime}
          eventId={event.id}
          canManage={event.userIsFacilitator}
          onCreateMeetingTimes={onCreateMeetingTimes}
          isCreatingMeetingTimes={isCreatingMeetingTimes}
          availableSeries={event.series}
        />
      )}

      {selectedMeetingTime && selectedMeetingTimeDetails ? (
        <RSVPManagement
          event={event}
          onRSVP={onRSVP}
          onCancelRSVP={onCancelRSVP}
          onApprovePending={onApprovePending}
          isApprovingPending={isApprovingPending}
          isUserAttending={isUserAttending}
          attendees={attendees}
          selectedMeetingTime={{
            beginMeetingTime: selectedMeetingTimeDetails.beginMeetingTime,
            capacity: selectedMeetingTimeDetails.capacity ?? 0,
            endMeetingTime: selectedMeetingTimeDetails.endMeetingTime,
            id: selectedMeetingTime.id,
            isPendingApproval: selectedMeetingTimeDetails.isPendingApproval,
            series: selectedMeetingTimeDetails.series,
          }}
          onDeleteMeetingTime={onDeleteMeetingTime}
          data-testid="rsvp-management"
        />
      ) : (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }} data-testid="event-time-details-title">
            {STRINGS.EVENT_TIME_DETAILS}
          </Typography>
          <Typography variant="body1" color="text.secondary" data-testid="event-time-details-message">
            {STRINGS.SELECT_EVENT_TIME}
          </Typography>
        </Paper>
      )}
    </>
  );
};
